import { IconName } from "@pathwright/pathicons"
import Pathicon from "@pathwright/ux/src/components/pathicon/Pathicon"
import { ReactNode } from "react"
import { CoverPageContributorImmersive } from "./CoverPageContributors"
import { CoverPageOfferImmersive } from "./CoverPageOffer"
import { CoverPageOverviewImmersive } from "./CoverPageOverview"
import { ImmersivePreviewContainer } from "./CoverPagePathPreview"
import CoverPageRelated from "./CoverPageRelated"
import { CoverPageTrailerImmersive } from "./Trailer/Trailer"
import { CoverPageContextType, ImmersiveRoute } from "./types"

// This (admittedly somewhat complex) configuration allows us to
// define Immersive route configuration in a single place.
// Used by React Router and for UI navigation.

export type ImmersiveRouteConfig = {
  key:
    | "register"
    | "trailer"
    | "contributors"
    | "contributor"
    | "previews"
    | "preview"
    | "offers"
    | "offer"
    | "overview"
    | "related"
  // React Router path pattern(s) used for route config
  pathRe: string | string[]
  // Build a navigate-able path from base and an ImmersiveRoute payload
  getPath: (args: { baseCover: string; route: ImmersiveRoute }) => string | null
  // Returns true when this route is currently active;
  // designed to be used by route handlers
  isActive: (args: { matchPath: string; params?: any }) => boolean
  // Build the ImmersiveRoute payload from route params
  getImmersiveRoute: (params: any) => ImmersiveRoute | null
  // Get immersive routes for routes that are actually active (based on context data)
  getNavigableImmersiveRoutes: (
    coverPageContext: CoverPageContextType
  ) => ImmersiveRoute[]
  // Render function for the immersive content
  render: (args?: any) => JSX.Element
  // Should this route be rendered at all?
  shouldRender?: (coverPageContext: CoverPageContextType) => boolean
  // If displayAsNavLabel is true, this label will be displayed in the navigation
  navLabel?: string | ReactNode
  // Icon to display in the navigation
  navIcon?: IconName
  // Whether to display a link to this route in the navigation
  displayAsNavLabel?:
    | boolean
    | ((coverPageContext: CoverPageContextType) => boolean)
  // Groups routes that should highlight the same nav item
  navGroup?: string
  // Maximum width of the immersive content for this route
  maxWidth?: string | number
}

export const immersiveRoutes: ImmersiveRouteConfig[] = [
  {
    key: "overview",
    navLabel: "Overview",
    navIcon: "list-one-column-fill",
    displayAsNavLabel: ({ resource }) => !!resource.description,
    pathRe: "overview/",
    render: () => <CoverPageOverviewImmersive />,
    shouldRender: ({ resource }) => !!resource.description,
    isActive: ({ matchPath }) => matchPath.includes("/overview/"),
    getPath: ({ baseCover }) => `${baseCover}/overview/`,
    getImmersiveRoute: () => ({ name: "overview" }),
    getNavigableImmersiveRoutes: ({ resource }) =>
      !!resource.description ? [{ name: "overview" }] : [],
    maxWidth: "650px"
  },
  {
    key: "trailer",
    navLabel: "Trailer",
    navIcon: "video",
    pathRe: "trailer/",
    displayAsNavLabel: ({ resource }) => !!resource.trailer_embed_code,
    render: () => <CoverPageTrailerImmersive />,
    shouldRender: ({ resource }) => !!resource.trailer_embed_code,
    isActive: ({ matchPath }) => matchPath.includes("/trailer/"),
    getPath: ({ baseCover }) => `${baseCover}/trailer/`,
    getImmersiveRoute: () => ({ name: "trailer" }),
    getNavigableImmersiveRoutes: ({ resource }) =>
      !!resource.trailer_embed_code ? [{ name: "trailer" }] : [],
    maxWidth: "900px"
  },
  // {
  //   key: "contributors",
  //   navLabel: "Contributors",
  //   navIcon: "avatar-circle-fill",
  //   pathRe: "contributors/",
  //   shouldRender: ({ contributors }) => contributors?.length > 0,
  //   render: ({ id }: { id: number }) => (
  //     <CoverPageContributorImmersive id={id} />
  //   ),
  //   isActive: ({ matchPath }) => matchPath.includes("/contributors/"),
  //   getPath: ({ baseCover }) => `${baseCover}/contributors/`,
  //   getImmersiveRoute: () => ({ name: "contributors" }),
  //   getNavigableImmersiveRoutes: () => [],
  //   displayAsNavLabel: ({ contributors }) => contributors?.length > 0
  // },
  {
    key: "contributor",
    navLabel: "Contributors",
    navGroup: "contributors",
    navIcon: "avatar-circle-fill",
    displayAsNavLabel: ({ contributors }) => contributors?.length > 0,
    pathRe: "contributor/:id(\\d+)/",
    shouldRender: ({ contributors }) => contributors?.length > 0,
    render: ({ id }: { id: number }) => (
      <CoverPageContributorImmersive id={id} />
    ),
    isActive: ({ matchPath, params }) =>
      matchPath.includes("/contributor/") && !!params?.id,
    getPath: ({ baseCover, route }) =>
      route.name === "contributor"
        ? `${baseCover}/contributor/${Number(route.id)}/`
        : null,
    getImmersiveRoute: (params: any) => {
      if (params?.id == null) return null
      return {
        name: "contributor",
        id: Number(params.id)
      }
    },
    getNavigableImmersiveRoutes: ({ contributors }) =>
      contributors?.length > 0
        ? contributors.map((c) => ({
            name: "contributor",
            id: c.id as number
          }))
        : [],
    maxWidth: "650px"
  },
  // {
  //   key: "previews",
  //   navLabel: "Preview",
  //   navIcon: "path",
  //   pathRe: "previews/",
  //   shouldRender: ({ previewNavItems }) => previewNavItems?.length > 0,
  //   render: ({ id }: { id: number | "path" }) => (
  //     <ImmersivePreviewContainer id={id} />
  //   ),
  //   isActive: ({ matchPath }) => matchPath.includes("/previews/"),
  //   getPath: ({ baseCover }) => `${baseCover}/previews/`,
  //   getImmersiveRoute: () => ({ name: "previews" }),
  //   getNavigableImmersiveRoutes: () => [],
  //   displayAsNavLabel: true
  // },
  {
    key: "preview",
    navLabel: "Preview",
    navIcon: "path",
    navGroup: "previews",
    displayAsNavLabel: ({ previewNavItems }) => previewNavItems?.length > 0,
    pathRe: "preview/:id(path|\\d+)/",
    shouldRender: ({ previewNavItems }) => previewNavItems?.length > 0,
    render: ({ id }: { id: number | "path" }) => (
      <ImmersivePreviewContainer id={id} />
    ),
    isActive: ({ matchPath, params }) =>
      matchPath.includes("/preview/") && !!params?.id,
    getPath: ({ baseCover, route }) =>
      route.name === "preview" && route.id != null
        ? `${baseCover}/preview/${route.id}/`
        : null,
    getImmersiveRoute: (params: any) => {
      if (params?.id == null) return null
      const raw = params.id
      const id = raw === "path" ? "path" : Number(raw)
      return { name: "preview", id }
    },
    getNavigableImmersiveRoutes: ({ previewNavItems }) =>
      previewNavItems?.length > 0
        ? previewNavItems.map((item) => ({
            name: "preview",
            id: item.source_id
          }))
        : [],
    maxWidth: "800px"
  },
  // {
  //   key: "offers",
  //   navLabel: (
  //     <>
  //       <Pathicon icon="plus" /> Join
  //     </>
  //   ) as ReactNode,
  //   navIcon: "group",
  //   pathRe: "offers/",
  //   shouldRender: ({ offers }) => offers?.length > 0,
  //   render: ({ id }: { id: number }) => (
  //     <CoverPageOfferImmersive id={id} />
  //   ),
  //   isActive: ({ matchPath }) => matchPath.includes("/offers/"),
  //   getPath: ({ baseCover }) => `${baseCover}/offers/`,
  //   getImmersiveRoute: () => ({ name: "offers" }),
  //   getNavigableImmersiveRoutes: () => [],
  //   displayAsNavLabel: ({ offers }) => offers?.length > 0
  // },
  {
    key: "offer",
    navLabel: (
      <>
        <Pathicon icon="plus" /> Join
      </>
    ) as ReactNode,
    navIcon: "group",
    navGroup: "offers",
    displayAsNavLabel: ({ offers }) => offers?.length > 0,
    pathRe: "offer/:id(\\d+)/",
    shouldRender: ({ offers }) => offers?.length > 0,
    render: ({ id }: { id: number }) => <CoverPageOfferImmersive id={id} />,
    isActive: ({ matchPath, params }) =>
      matchPath.includes("/offer/") && !!params?.id,
    getPath: ({ baseCover, route }) =>
      route.name === "offer" ? `${baseCover}/offer/${Number(route.id)}/` : null,
    getImmersiveRoute: (params: any) => {
      if (params?.id == null) return null
      return { name: "offer", id: Number(params.id) }
    },
    getNavigableImmersiveRoutes: ({ offers }) =>
      offers?.length > 0
        ? offers.map((o) => ({
            name: "offer",
            id: o.id as number
          }))
        : [],
    maxWidth: "650px"
  },
  {
    key: "register",
    navGroup: "offers",
    pathRe: [
      "register/",
      "register/thank-you/",
      "register/order/:orderId(\\d+)/thank-you/"
    ],
    shouldRender: ({ offers }) => offers?.length > 0,
    render: ({ renderRegisterContent }) => renderRegisterContent?.(),
    isActive: ({ matchPath }) =>
      matchPath.includes("/register/") || matchPath.includes("register/"),
    getPath: ({ baseCover }) => `${baseCover}/register/`,
    getImmersiveRoute: () => ({ name: "register" }),
    // Register route is not navigable
    getNavigableImmersiveRoutes: () => []
  },
  {
    key: "related",
    navIcon: "ellipses-o",
    pathRe: "related/",
    navLabel: "More...",
    displayAsNavLabel: ({ relatedResources }) => relatedResources?.length > 0,
    shouldRender: ({ relatedResources }) => relatedResources?.length > 0,
    render: () => <CoverPageRelated />,
    isActive: ({ matchPath }) => matchPath.includes("/related/"),
    getPath: ({ baseCover }) => `${baseCover}/related/`,
    getImmersiveRoute: () => ({ name: "related" }),
    getNavigableImmersiveRoutes: ({ relatedResources }) =>
      relatedResources?.length > 0 ? [{ name: "related" }] : [],
    maxWidth: "900px"
  }
]
